// @ts-check

import { get_schedules } from '~/api/zero-api';
import { ScheduleActionCell, ScheduleNameCell, ScheduleTypeCell, ScheduleTemplateCell, ScheduleTeamsCell, ScheduleRespondersCell, ScheduleAssignmentsCell, ScheduleCompletedCell, ScheduleStatusCell } from '~/components/Forms/ScheduleDataGrid/ScheduleDataGrid.cells';
import { AgGridFilter } from '~/other/agGridHelper';
import { memoize } from "lodash-es";
import { generateDataGridEventNames } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';
import { getScheduleFrequency, dateFormatterNoTime, dateFormatterFromString } from '~/other/Helper';
import dayjs from "dayjs";

export const EventNames = generateDataGridEventNames('ScheduleDataGrid');

export const DEFAULT_SORT_MODEL = [
    {
        colId: "name",
        sort: "asc",
    },
];

export function getSortByColumnName(colName) {
    switch (colName) {
        case'form.name': return 'form';
        default: return colName;
    }
}

/**
 * @param {string} pageType
 * @returns {AgGrid.GridOptions['columnDefs']}
 */
export function getColumnDefs(pageType) {
    /** @type {AgGrid.GridOptions['columnDefs']} */
    let defs = [
        {
            headerName: "",
            field: "_actions_",
            width: 50,
            cellRenderer: ScheduleActionCell,
            resizable: false,
            sortable: false,
            suppressMovable: true,
            lockPosition: "left",
            cellStyle: {
                padding: 0,
            }
        },
        {
            headerName: "Schedule",
            field: "name",
            initialSort: "asc",
            cellRenderer: ScheduleNameCell,
            width: 175,
            ...AgGridFilter.textNoBlankOptions,
        },
        {
            headerName: "Frequency",
            field: "repeat_period",
            width: 150,
            sortable: false,
            valueFormatter: ({data: schedule}) => {
                if (!schedule) {
                    return "";
                }
                let customStartTimestamp = schedule.custom_start_utc_timestamp;
                if (!customStartTimestamp) {
                    const dt = new Date();
                    customStartTimestamp = dt.setUTCHours(10, 0, 0, 0) / 1000;
                }

                const customStartTime = dayjs.unix(customStartTimestamp).format("hA");
                return getScheduleFrequency(schedule.repeat_period) + " at " + customStartTime;
            },
        },
        {
            headerName: pageType === "lms" ? "Course" : "Template",
            field: "form",
            width: 180,
            cellRenderer: ScheduleTemplateCell,
            ...AgGridFilter.textNoBlankOptions,
        },
        {
            headerName: "Teams",
            field: "teams.length",
            width: 95,
            sortable: false,
            cellRenderer: ScheduleTeamsCell,
            valueFormatter: ({data: schedule}) => {
                if (!schedule) {
                    return "";
                }
                return schedule.teams.map(team => team.name).join(", ");
            },
            cellStyle: { textAlign: "center" },
        },
        {
            headerName: "Assignees",
            field: "subscribers_count",
            width: 155,
            sortable: false,
            cellRenderer: ScheduleRespondersCell,
            valueFormatter: ({data: schedule}) => {
                if (!schedule) {
                    return "";
                }
                return schedule.subscribers.map(subscriber => subscriber.full_name).join(", ");
            },
            cellStyle: { textAlign: "center" },
        },
        {
            headerName: "Type",
            field: "type",
            width: 75,
            sortable: false,
            cellRenderer: ScheduleTypeCell,
            valueFormatter: ({value}) => {
                if (typeof value !== "string") {
                    return "";
                }
                return value === "regular" ? "Regular" : "Shared";
            },
        },
        {
            headerName: "Assignments",
            field: "assigns_count",
            width: 135,
            sortable: false,
            cellRenderer: ScheduleAssignmentsCell,
            cellStyle: { textAlign: "center" },
        },
        {
            headerName: "Completed",
            field: "assigns_completed",
            width: 120,
            sortable: false,
            cellRenderer: ScheduleCompletedCell,
            cellStyle: { textAlign: "center" },
        },
        {
            headerName: "Status",
            field: "enabled",
            width: 95,
            sortable: false,
            cellRenderer: ScheduleStatusCell,
            valueFormatter: ({value}) => {
                if (typeof value !== "boolean") {
                    return "";
                }
                return value ? "Active" : "Paused";
            },
        },
        {
            headerName: "Last Edited",
            field: "edited_at",
            width: 175,
            valueFormatter: ({value}) => {
                if (typeof value !== "number") {
                    return "";
                }
                return dateFormatterNoTime(value);
            },
            ...AgGridFilter.date,
        },
        {
            headerName: "Start Date",
            field: "start_date",
            width: 180,
            valueFormatter: ({value}) => {
                if (typeof value !== "string" || !value) {
                    return "";
                }
                return dateFormatterFromString(value);
            },
            ...AgGridFilter.date,
        },
        {
            headerName: "End Date",
            field: "end_date",
            width: 180,
            valueFormatter: ({value}) => {
                if (typeof value !== "string" || !value) {
                    return "";
                }
                return dateFormatterFromString(value);
            },
            ...AgGridFilter.date,
        },
    ];

    return defs;
}

/**
 * @param {URLSearchParams} queryParams 
 * @returns {Promise<{schedules: any[], totalSchedules: number}>}
 */
async function _loadSchedules(queryParams) {
    const response = await get_schedules(`?${queryParams}`);
    const content = await response.json();
    const schedules = content.schedulers ?? [];
    const totalSchedules = content.total_schedulers ?? 0;

    return { schedules, totalSchedules };
}

export const loadSchedules = memoize(_loadSchedules, (queryParams) => queryParams.toString());

/**
 * 
 * @param {any} schedule 
 * @param {string} orgId 
 */
export function getScheduleAnalyticsLink(schedule, orgId) {
    return `/${orgId}/home/manage_schedules/analytics/${schedule.scheduler_uuid}`;
} 